<template>
    <section class="section">
        <div class="container">
            <div class="columns is-multiline">
                <div class="column is-full">
                    <h1 class="title">{{ translate("admin_schedules_list.title") }}</h1>
                </div>

                <div class="column is-mobile">
                    <div class="buttons">
                        <button class="button is-primary" @click="addNewSchedule">
                            <span class="icon">
                                <i class="fa fa-plus" />
                            </span>
                            <span>
                                {{ translate("admin_schedules_list.add_new") }}
                            </span>
                        </button>
                        <button class="button" @click="scheduleSettings?.open()">
                            <span class="icon">
                                <i class="fa fa-wrench" />
                            </span>
                            <span>
                                {{ translate("admin_schedules_list.settings") }}
                            </span>
                        </button>
                        <button class="button" @click="approximateSchedulesTime?.open()">
                            <span class="icon">
                                <i class="fa fa-chart-bar"></i>
                            </span>
                            <span>
                                {{ translate("admin_schedules_list.estimate_total_time") }}
                            </span>
                        </button>
                        <a class="button" :href="scheduleStatusUrl">
                            <span class="icon">
                                <i class="fa fa-clock" />
                            </span>
                            <span>
                                {{ translate("admin_schedules_list.delay_of_active_schedule") }}
                            </span>
                        </a>
                        <button class="button" @click="shareFightsQueueModal?.open()">
                            <span class="icon">
                                <i class="fas fa-share-square" />
                            </span>
                            <span>
                                {{ translate("admin_share_fights_queue.title")
                                }}<template v-if="store.sharePublicFightsQueue !== null"
                                    >:
                                    <span
                                        class="has-text-weight-bold"
                                        :class="store.sharePublicFightsQueue ? 'has-text-success' : 'has-text-danger'"
                                        >{{
                                            translate(
                                                store.sharePublicFightsQueue
                                                    ? "admin_share_fights_queue.public"
                                                    : "admin_share_fights_queue.hidden",
                                            )
                                        }}</span
                                    ></template
                                >
                            </span>
                        </button>
                        <LoadingSpinner2 v-if="isLoading" class="is-medium" />
                    </div>
                </div>
            </div>

            <div class="columns">
                <div class="column">
                    <div class="box has-no-glow-effect schedule-card" v-for="schedule in store.schedules" :key="schedule.id">
                        <div class="is-flex is-justify-content-space-between is-align-items-flex-start schedule-card__header">
                            <a class="title is-5 is-size-6-mobile mb-0" :href="getShowScheduleUrl(schedule.id)">
                                {{ schedule.name }}
                            </a>
                            <span
                                v-if="store.activeSchedule.id === schedule.id"
                                class="tag is-success is-size-7-mobile ml-2 schedule-card__active-tag"
                            >
                                {{ translate("admin_schedules_list.active") }}
                            </span>
                        </div>

                        <div class="schedule-card__details is-size-7-mobile mt-3">
                            <div class="is-flex is-justify-content-space-between schedule-card__row">
                                <span class="has-text-grey-light">{{ translate("admin_schedules_list.start_time") }}</span>
                                <span class="has-text-white has-text-right">
                                    {{ convertToTimezone(schedule.startTime, eventTimezone, "yyyy-MM-dd HH:mm") }}
                                </span>
                            </div>
                            <div class="is-flex is-justify-content-space-between schedule-card__row">
                                <span class="has-text-grey-light">{{ translate("admin_schedules_list.sharing") }}</span>
                                <span class="has-text-white has-text-right">{{ getSharingTypeText(schedule.sharing) }}</span>
                            </div>
                            <div class="is-flex is-justify-content-space-between schedule-card__row">
                                <span class="has-text-grey-light">{{ translate("admin_schedules_list.planned_version") }}</span>
                                <span class="has-text-success has-text-right" v-if="schedule.plannedVersionAvailable">
                                    <span class="icon-text is-align-items-center">
                                        <span class="icon"><i class="fas fa-check" /></span>
                                        <span>{{ translate("admin_schedules_list.planned_version.available") }}</span>
                                    </span>
                                </span>
                                <span class="has-text-danger has-text-right" v-else>
                                    <span class="icon-text is-align-items-center">
                                        <span class="icon"><i class="fas fa-times" /></span>
                                        <span>{{ translate("admin_schedules_list.planned_version.not_available") }}</span>
                                    </span>
                                </span>
                            </div>
                            <div v-if="schedule.plannedVersionAvailable" class="has-text-right mt-1">
                                <a class="is-size-7" @click="erasePlannedVersion(schedule.id)">
                                    {{ translate("common.click_to_remove") }}
                                </a>
                            </div>
                        </div>

                        <div class="mt-4 buttons are-small schedule-card__actions">
                            <button class="button" @click="editSchedule(schedule)">
                                <span class="icon">
                                    <i class="far fa-edit" />
                                </span>
                                <span>
                                    {{ translate("common.edit") }}
                                </span>
                            </button>

                            <a class="button" :href="getShowScheduleUrl(schedule.id)">
                                <span class="icon">
                                    <i class="fa fa-tasks" />
                                </span>
                                <span>{{ translate("admin_schedules_list.manage") }}</span>
                            </a>

                            <button class="button" @click="activateSchedule(schedule.id)">
                                <span class="icon">
                                    <i class="fa fa-check" />
                                </span>
                                <span>
                                    {{ translate("admin_schedules_list.activate") }}
                                </span>
                            </button>

                            <button class="button" @click="openSharingModal(schedule)">
                                <span class="icon">
                                    <i class="fa fa-share" />
                                </span>
                                <span>
                                    {{ translate("common.share") }}
                                </span>
                            </button>

                            <button class="button is-danger" @click="deleteSchedule(schedule.id)">
                                <span class="icon">
                                    <i class="fa fa-trash-alt" />
                                </span>
                                <span>
                                    {{ translate("common.delete") }}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="columns">
                <div class="column">
                    <div class="buttons">
                        <button class="button is-success" @click="fightsGenerationModal?.open()" :disabled="store.schedules.length === 0">
                            <span class="icon">
                                <i class="fa fa-plus" />
                            </span>
                            <span>
                                {{ translate("admin_schedules_list.generate_fights") }}
                            </span>
                        </button>
                        <button class="button" @click="autoOrderingModal?.open()" :disabled="store.schedules.length === 0">
                            <span class="icon">
                                <i class="fa fa-random" />
                            </span>
                            <span>
                                {{ translate("admin_auto_order.sorting_fights") }}
                            </span>
                        </button>

                        <button class="button" @click="fightsEnumerationModal?.open()">
                            <span class="icon">
                                <i class="fas fa-list-ol"></i>
                            </span>
                            <span>
                                {{ translate("page.schedule.enumerate_fights_label") }}
                            </span>
                        </button>

                        <button class="button is-danger" @click="deleteFightsModal?.open()" :disabled="store.schedules.length === 0">
                            <span class="icon">
                                <i class="fa fa-trash" />
                            </span>
                            <span>
                                {{ translate("admin_schedules_list.delete_fights") }}
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <ScheduleDetailsModal
            ref="scheduleNameModal"
            :event-timezone="eventTimezone"
            :event-start-date="eventStartDate"
            :event-end-date="eventEndDate"
        />

        <ScheduleSettings ref="scheduleSettings" :event-id="eventId" />

        <ApproximateSchedulesTime ref="approximateSchedulesTime" :event-id="eventId" />

        <ScheduleSharingModal
            ref="sharingModal"
            :sharing-types="store.sharingTypes"
            @offer-fights-queue-sharing="shareFightsQueueModal?.open()"
        />

        <ShareFightsQueueModal ref="shareFightsQueueModal" :event-slug="eventSlug" />

        <FightsGenerationModal ref="fightsGenerationModal" :event-id="eventId" />

        <AutoOrderingModal ref="autoOrderingModal" :event-id="eventId" />

        <FightsEnumerationModal ref="fightsEnumerationModal" :event-id="eventId" />

        <DeleteFightsModal ref="deleteFightsModal" :event-id="eventId" />
    </section>
</template>

<script setup lang="ts">
    import { nextTick, onMounted, ref } from "vue";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import { successToast } from "../Common/toasts";
    import { convertToTimezone } from "../Common/dateTimeUtils";
    import urls from "../Urls/urls";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import ScheduleDetailsModal from "./ScheduleDetailsModal.vue";
    import ScheduleSettings from "./ScheduleSettings.vue";
    import ApproximateSchedulesTime from "./ApproximateSchedulesTime.vue";
    import ScheduleSharingModal from "./ScheduleSharingModal.vue";
    import ShareFightsQueueModal from "./ShareFightsQueueModal.vue";
    import FightsGenerationModal from "./FightsGenerationModal.vue";
    import AutoOrderingModal from "./AutoOrderingModal.vue";
    import FightsEnumerationModal from "./FightsEnumerationModal.vue";
    import DeleteFightsModal from "./DeletingFights/DeleteFightsModal.vue";
    import { useSchedulesListingStore } from "./useSchedulesListingStore";
    import { Schedule } from "../Common/Schedule/types";
    import history from "../Common/history";

    const props = defineProps<{
        eventId: number;
        eventSlug: string;
        eventStartDate: string;
        eventEndDate: string;
        eventTimezone: string;
        scheduleStatusUrl: string;
    }>();

    const store = useSchedulesListingStore();
    store.eventId = props.eventId;

    const isLoading = ref(false);

    const scheduleNameModal = ref<InstanceType<typeof ScheduleDetailsModal> | null>(null);
    const scheduleSettings = ref<InstanceType<typeof ScheduleSettings> | null>(null);
    const approximateSchedulesTime = ref<InstanceType<typeof ApproximateSchedulesTime> | null>(null);
    const sharingModal = ref<InstanceType<typeof ScheduleSharingModal> | null>(null);
    const shareFightsQueueModal = ref<InstanceType<typeof ShareFightsQueueModal> | null>(null);
    const fightsGenerationModal = ref<InstanceType<typeof FightsGenerationModal> | null>(null);
    const autoOrderingModal = ref<InstanceType<typeof AutoOrderingModal> | null>(null);
    const fightsEnumerationModal = ref<InstanceType<typeof FightsEnumerationModal> | null>(null);
    const deleteFightsModal = ref<InstanceType<typeof DeleteFightsModal> | null>(null);

    const historyInstance = history.getInstance(window);

    onMounted(() => {
        const openShareFightsQueueRequested = consumeShareFightsQueueDeepLink();

        refreshListing().then(() => {
            // Open only after the listing (incl. the sharing status) loaded, otherwise the modal body is empty.
            if (openShareFightsQueueRequested) {
                nextTick(() => shareFightsQueueModal.value?.open());
            }
        });
    });

    // The dashboard checklist links here with ?openShareFightsQueueModal=1. The parameter is removed so a refresh
    // does not reopen the modal.
    function consumeShareFightsQueueDeepLink(): boolean {
        if (historyInstance.getQueryParam("openShareFightsQueueModal") !== "1") {
            return false;
        }

        historyInstance.setQueryParam("openShareFightsQueueModal", null);

        return true;
    }

    function refreshListing() {
        isLoading.value = true;

        return store
            .refreshListing()
            .catch(handleError)
            .finally(() => {
                isLoading.value = false;
            });
    }

    function deleteSchedule(id: number) {
        return store.deleteSchedule(id).catch(handleError);
    }

    function activateSchedule(id: number) {
        return store
            .activateSchedule(id)
            .then(() => {
                successToast(translate("admin_schedules_list.schedule_activated"));
            })
            .catch(handleError);
    }

    function erasePlannedVersion(id: number) {
        if (!confirm(translate("common.are_you_sure"))) {
            return;
        }

        return store.erasePlannedVersion(id).catch(handleError);
    }

    function getShowScheduleUrl(scheduleIdValue: number) {
        return urls.eventAdmin().scheduleManagementPage(props.eventId, scheduleIdValue);
    }

    function addNewSchedule() {
        scheduleNameModal.value?.open();
    }

    function editSchedule(schedule: Schedule) {
        scheduleNameModal.value?.open(schedule);
    }

    function openSharingModal(schedule: Schedule) {
        sharingModal.value?.open(schedule);
    }

    function getSharingTypeText(value: number) {
        const foundSharingType = store.sharingTypes.find(sharingType => sharingType.value === value);

        return foundSharingType?.text ?? "";
    }
</script>

<style scoped>
    .schedule-card__header .title {
        word-break: break-word;
    }

    .schedule-card__active-tag {
        flex-shrink: 0;
        white-space: nowrap;
    }

    @media screen and (min-width: 769px) {
        .schedule-card__header {
            justify-content: flex-start !important;
        }
    }

    .schedule-card__row {
        gap: 1rem;
        padding: 0.35rem 0;
        border-bottom: 1px solid var(--theme-primary-15);
    }

    .schedule-card__row:last-of-type {
        border-bottom: none;
    }

    .schedule-card__row > span:last-child {
        flex-shrink: 0;
    }

    @media screen and (max-width: 768px) {
        .schedule-card__actions {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 0.5rem;
        }

        .schedule-card__actions .button {
            margin: 0;
            width: 100%;
            font-size: 0.75rem;
        }
    }
</style>
